/**
 * Which view can show what this document produced.
 *
 * Preview is not an image. `rendered` is the only mode with a printed page, and the only
 * one where region geometry means anything; the rest are views over the block structure the
 * parse recovered, which for those formats is exact rather than inferred. A format with no
 * printed page is not a failure and must not read as one — which is what this replaces.
 *
 * The switch is exhaustive with no `default`: a mode added to the API must become a
 * TypeScript error here rather than a blank pane at runtime.
 */
import { useQuery } from "@tanstack/react-query";
import { get } from "../lib/api";
import { ApiError } from "../lib/api";
import { DocumentView } from "./DocumentView";
import { SheetView } from "./SheetView";
import { SlidesView } from "./SlidesView";
import { Empty, Failed, Loading } from "./ui";
import type { DocumentStructure, PreviewMode } from "../lib/types";

function useStructure(documentId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["structure", documentId],
    queryFn: () => get<DocumentStructure>(`/v1/documents/${documentId}/structure`),
    enabled,
    retry: false,
  });
}

export function StructurePreview({
  documentId,
  mode,
  fallback,
}: {
  documentId: string;
  mode: Exclude<PreviewMode, "rendered" | "none">;
  /** Shown when no structure was stored — a document parsed before structure was kept. */
  fallback: React.ReactNode;
}) {
  const structure = useStructure(documentId, true);

  if (structure.isPending) return <Loading what="document structure" />;
  if (structure.isError) {
    // 404 is the ordinary "parsed before this existed" case, not a failure to report.
    if (structure.error instanceof ApiError && structure.error.status === 404) {
      return <>{fallback}</>;
    }
    return <Failed error={structure.error} what="this document's structure" />;
  }

  switch (mode) {
    case "sheet":
      return <SheetView structure={structure.data} />;
    case "slides":
      return <SlidesView structure={structure.data} />;
    case "document":
      return <DocumentView structure={structure.data} />;
  }
}

export function NoPreview({ reason }: { reason?: string | null }) {
  return (
    <Empty
      title="nothing to preview"
      hint={reason ?? "the parse recorded no reason"}
    />
  );
}
